<template>
	<Provider>
		<component :is="layoutComponent" :class="[`theme-${themeName}`, `layout-${layout}`, themeName]">
			<RouterView v-slot="{ Component }">
				<transition :name="`router-${routerTransition}`" mode="out-in" appear>
					<component
						:is="Component"
						:key="forceRefresh"
						:class="[`theme-${themeName}`, `layout-${layout}`, themeName]"
					/>
				</transition>
			</RouterView>
		</component>
		<audio id="generalAudioPlayer">
			Your browser does not support the <code>audio</code> element. 
		</audio>
		<!-- Cold start only: initDB + short delay (local `loading`). Not mounted per route. -->
		<SplashScreen :loading="loading" />
		<!-- Single global overlay; inner views set `useEventsStore().appShellBusy` — do not add this component elsewhere. -->
		<AppShellSpinner />
		<!-- <SearchDialog v-if="isLogged" /> -->
		<!-- <LayoutSettings /> -->
	</Provider>
	<!-- Toast for Point Changes -->
	<Toast />
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, ref, type Component } from "vue"
import { useMainStore } from "@/stores/main"
import { useAuthStore } from "@/stores/auth"
import { useThemeStore } from "@/stores/theme"
import { useNavigationStore } from "@/stores/navigation"
import { useNotificationStore } from "@/stores/apps/notification"
import VerticalNav from "@/app-layouts/VerticalNav/index.vue"
import HorizontalNav from "@/app-layouts/HorizontalNav/index.vue"
import Blank from "@/app-layouts/Blank/index.vue"
import Provider from "@/app-layouts/common/Provider.vue"
import SplashScreen from "@/app-layouts/common/SplashScreen.vue"
import AppShellSpinner from "@/components/common/AppShellSpinner.vue"
import LayoutSettings from "@/components/common/LayoutSettings.vue"
import SearchDialog from "@/components/common/SearchDialog.vue"
import { Layout, RouterTransition, type ThemeName } from "@/types/theme.d"
import { type RouteLocationNormalized, useRouter, useRoute } from "vue-router"
import "@/assets/scss/index.scss"
import { initDB } from "./utils/indexeddb"
import Toast from "primevue/toast"
import { useToast } from "primevue/usetoast"
import { setToastInstance } from "@/utils/notifications"

const router = useRouter()
const toast = useToast()
setToastInstance(toast)
const loading = ref(true)

const layoutComponents = {
	VerticalNav,
	HorizontalNav,
	Blank
}

const themeStore = useThemeStore()
const mainStore = useMainStore()
const authStore = useAuthStore()
const navigationStore = useNavigationStore()
const notificationStore = useNotificationStore();

const forceLayout = ref<Layout | null>(null)
const forceRefresh = computed<number>(() => mainStore.forceRefresh)
const layout = computed<Layout>(() => themeStore.layout)
const layoutComponent = computed<Component>(() => layoutComponents[forceLayout.value || layout.value])
const routerTransition = computed<RouterTransition>(() => themeStore.routerTransition)
const themeName = computed<ThemeName>(() => themeStore.themeName)
const isLogged = computed(() => authStore.isLogged)

function checkForcedLayout(route: RouteLocationNormalized) {
	if (route.meta?.forceLayout) {
		forceLayout.value = route.meta.forceLayout
	} else {
		forceLayout.value = null
	}
}

router.beforeEach(route => {
	checkForcedLayout(route)
})

onBeforeMount(async () => {
	checkForcedLayout(useRoute())
	await initDB()
	setTimeout(() => {
		loading.value = false
	}, 500)
})


</script>
